import type { PropFootprint } from "daedalus-core";
import { AUTHORING_CELL, FOOT_HALF_WIDTH, FOOT_HEIGHT } from "../movement/step.ts";
import { groundCellInFrame } from "./ground.ts";

/**
 * A shrine's reach (ADR-096 decision 10): the cells a character steps into to discover it, and the safe zone the
 * editor's stamp paints (decision 38). It is the author's own Sanctuary zone around the Old Shrine, measured: 237
 * collision cells, a filled ellipse 21 cells wide and 15 tall, its middle column on the column of the obelisk's base
 * and its tenth row on the base's row, so it reaches 9 rows above the ground point and 5 below.
 */

/**
 * A cell mask as row runs: each row's first column and length from the mask's left edge, the rows from the top, and
 * the mask's cell that lies on a thing's ground point.
 */
export interface ReachMask {
  readonly columns: number;
  readonly rows: number;
  readonly originColumn: number;
  readonly originRow: number;
  readonly runs: readonly (readonly [number, number])[];
}

const RUNS: readonly (readonly [number, number])[] = Object.freeze(
  ([[8, 5], [5, 11], [3, 15], [2, 17], [2, 17], [0, 21], [0, 21], [0, 21], [0, 21], [1, 19], [1, 19], [2, 17], [3, 15], [4, 13], [8, 5]] as const).map((run) => Object.freeze(run)),
);

/**
 * The shrine's reach.
 */
export const SHRINE_REACH: ReachMask = Object.freeze({ columns: 21, rows: 15, originColumn: 10, originRow: 9, runs: RUNS });

/**
 * How many cells the reach holds.
 */
export const SHRINE_REACH_CELLS = 237;

/**
 * Whether any cell of a character's feet box lies in the reach around a ground cell: the test for a marker shrine,
 * which has no frame to carry activation cells, and the same cells `shrineActivationCells` gives a frame. The box is
 * the one movement collides with (`FOOT_HALF_WIDTH`, `FOOT_HEIGHT`). Integer arithmetic over the box's one or two
 * rows; nothing is allocated.
 *
 * @param x The character's feet x in sixteenths.
 * @param y The character's feet y in sixteenths.
 * @param groundColumn The column of the cell the shrine's ground point lies in.
 * @param groundRow That cell's row.
 * @returns True when the box touches a cell of the reach; false for a position that is not finite.
 */
export function inShrineReach(x: number, y: number, groundColumn: number, groundRow: number): boolean {
  if (!Number.isFinite(x) || !Number.isFinite(y)) {
    return false;
  }
  const feetLeft = Math.floor((x - FOOT_HALF_WIDTH) / AUTHORING_CELL);
  const feetRight = Math.floor((x + FOOT_HALF_WIDTH - 1) / AUTHORING_CELL);
  const feetBottom = Math.floor((y - 1) / AUTHORING_CELL);
  const top = groundRow - SHRINE_REACH.originRow;
  const left = groundColumn - SHRINE_REACH.originColumn;
  for (let row = Math.floor((y - FOOT_HEIGHT) / AUTHORING_CELL); row <= feetBottom; row += 1) {
    const run = RUNS[row - top];
    if (run === undefined) {
      continue;
    }
    const first = left + run[0];
    if (feetRight >= first && feetLeft < first + run[1]) {
      return true;
    }
  }
  return false;
}

/**
 * The reach's cells around a ground cell, for a caller that lists them (a stamp, a test): row by row from the top,
 * each row from the left.
 *
 * @param groundColumn The column of the cell the ground point lies in.
 * @param groundRow That cell's row.
 * @returns The 237 cells as `[column, row]`.
 */
export function shrineReachCells(groundColumn: number, groundRow: number): [number, number][] {
  const cells: [number, number][] = [];
  RUNS.forEach(([start, length], index) => {
    for (let across = 0; across < length; across += 1) {
      cells.push([groundColumn - SHRINE_REACH.originColumn + start + across, groundRow - SHRINE_REACH.originRow + index]);
    }
  });
  return cells;
}

/**
 * The reach as a behaviour prop's activation cells (ADR-053 decision 8): `[column, rowsUp]` in the frame's own
 * unmirrored space, laid around the cell the frame's ground point falls in, so `inActivation` over them selects the
 * cells `inShrineReach` does around that ground point. The upgrade and the editor's stamp write these.
 *
 * @param footprint The footprint of the frame the shrine is placed on; one that blocks nothing takes the reach
 *   around its feet point.
 * @param tileSize Tile size in pixels.
 * @returns The 237 cells, row by row from the top, each row from the left.
 */
export function shrineActivationCells(footprint: PropFootprint, tileSize: number): [number, number][] {
  const ground = groundCellInFrame(footprint, tileSize);
  const cells: [number, number][] = [];
  RUNS.forEach(([start, length], index) => {
    for (let across = 0; across < length; across += 1) {
      cells.push([ground.column - SHRINE_REACH.originColumn + start + across, ground.rowsUp + SHRINE_REACH.originRow - index]);
    }
  });
  return cells;
}
